import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AVAILABLE_LOCALES, RTL_LOCALES } from '@vcip/help';
import { AR_DECK } from './decks/ar';
import { SV_COPY } from './decks/en';
import { HI_DECK } from './decks/hi';

/**
 * The svapp i18n runtime — forked VERBATIM from vCIP_droom_4.0/src/i18n/runtime.tsx (DEC-053,
 * Tier 2.5: a flat full-sentence deck lookup with one `{slot}` replace; no ICU engine on the
 * representative's low-bandwidth bundle).
 *
 * THE LOCALE SET IS DERIVED, NEVER RE-DECLARED (DEC-053's rider): offerable = (the console's
 * content-backed AVAILABLE_LOCALES) ∩ (svapp decks that are COMPLETE). A locale enters when it is
 * real in BOTH places and never before — no second hand-maintained list.
 *
 * VC-12 adds ONE condition for PACK-GATED languages (Arabic): the case's pack must name it (`packLocales`). And it
 * stamps `dir` beside `lang` on <html>, so an Arabic journey mirrors from the root.
 */

export type SvDeck = Readonly<Record<string, string>>;

export const DECKS: Readonly<Record<string, SvDeck>> = {
  en: SV_COPY,
  hi: HI_DECK,
  ar: AR_DECK,
};

/**
 * VC-12 — PACK-GATED languages. A complete deck is necessary but not sufficient: Arabic is offered ONLY when the case's
 * pack names it (`packLocales` on the exchange summary, be V223 — the Gulf packs say `[en, ar]`). No `packLocales` (every
 * India case: the key is absent) means no pack-gated language, so an India case's chooser and switcher are exactly what
 * they were before Arabic existed. The be refuses `PATCH /locale ar` on such a case too.
 */
export const PACK_GATED_LOCALES: readonly string[] = ['ar'];

/** The case's pack languages as the exchange summary carries them; null/undefined when the pack declares none. */
export type PackLocales = readonly string[] | null | undefined;

function packAllows(locale: string, packLocales: PackLocales): boolean {
  return !PACK_GATED_LOCALES.includes(locale) || (Array.isArray(packLocales) && packLocales.includes(locale));
}

/** Right-to-left scripts (the help system's list — one source of truth). */
export function isRtl(locale: string): boolean {
  return (RTL_LOCALES as readonly string[]).includes(locale);
}

export function dirOf(locale: string): 'rtl' | 'ltr' {
  return isRtl(locale) ? 'rtl' : 'ltr';
}


/** A deck counts only when it covers EVERY EN key — never a mixed-language screen. */
export function isDeckComplete(locale: string): boolean {
  const deck = DECKS[locale];
  if (!deck || locale === 'en') return locale === 'en';
  return Object.keys(SV_COPY).every((key) => {
    const value = deck[key];
    return typeof value === 'string' && value.trim() !== '';
  });
}

export function offerableLocales(packLocales?: PackLocales): string[] {
  return (AVAILABLE_LOCALES as readonly string[]).filter(
    (locale) => (locale === 'en' || isDeckComplete(locale)) && packAllows(locale, packLocales),
  );
}

/** Native names — how a speaker finds their own language. Never translated. */
export const NATIVE_NAMES: Readonly<Record<string, string>> = {
  en: 'English',
  hi: 'हिन्दी',
  bn: 'বাংলা',
  mr: 'मराठी',
  te: 'తెలుగు',
  ta: 'தமிழ்',
  gu: 'ગુજરાતી',
  kn: 'ಕನ್ನಡ',
  ml: 'മലയാളം',
  or: 'ଓଡ଼ିଆ',
  pa: 'ਪੰਜਾਬੀ',
  fr: 'Français',
  de: 'Deutsch',
  es: 'Español',
  pt: 'Português',
  ar: 'العربية',
};

/**
 * The camera screens' compact switcher labels (the bar's right slot): each language says its OWN short name, so a
 * Hindi-only reader finds "हिं", not "HI". Never translated; the full native name stays the button's accessible name.
 * A language with no entry falls back to its upper-cased code.
 */
export const SHORT_NAMES: Readonly<Record<string, string>> = {
  en: 'EN',
  hi: 'हिं',
  // VC-12: Arabic is offered on a Gulf case's camera bar too — in its own script, never "AR"
  ar: 'عربي',
};

/**
 * The chooser's eleven rows (SPEC_CAPTURE_UX_R2 §2.1), in the spec's order. A row is OFFERED only when its deck is complete
 * (the P10 rule — a language with no content behind it is a dead end): today en + hi; the other nine render nothing until
 * their decks land. NOTE (the spec's VERIFY): the platform's `AVAILABLE_LOCALES` (fr/de/es/pt/ar) are NOT Indic — they are
 * the help system's locales and do not count as coverage here.
 */
export const CHOOSER_LOCALES: readonly string[] = ['hi', 'bn', 'mr', 'te', 'ta', 'gu', 'kn', 'ml', 'or', 'pa', 'en'];

export function chooserLocales(packLocales?: PackLocales): string[] {
  const rows = CHOOSER_LOCALES.filter((l) => l !== 'en' && isDeckComplete(l));
  // VC-12: a pack-gated language the case's pack names joins the list, before English (which stays last)
  const gated = PACK_GATED_LOCALES.filter((l) => isDeckComplete(l) && packAllows(l, packLocales) && offerableLocales(packLocales).includes(l));
  return [...rows, ...gated, 'en'];
}

const STORAGE_KEY = 'vcip-svapp-locale';

interface LocaleContextValue {
  locale: string;
  /** 'rtl' for Arabic — also stamped on <html dir>. */
  dir: 'rtl' | 'ltr';
  /** What this case may offer: `offerableLocales(packLocales)`. */
  offered: readonly string[];
  packLocales: PackLocales;
  setLocale: (next: string) => void;
  t: (key: string, vars?: Record<string, string>) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

/** True when a language was CHOSEN on this device (the chooser is then not shown again — SPEC_CAPTURE_UX_R2 §2 acceptance). */
export function hasStoredLocale(packLocales?: PackLocales): boolean {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return !!stored && offerableLocales(packLocales).includes(stored);
  } catch {
    return false;
  }
}

/**
 * The language CHOSEN on this device, when it is still offerable — else null. VC-12: offerable for THIS case, so a
 * pack-gated language (Arabic) remembered from a Gulf case is never carried onto a case whose pack does not list it.
 */
export function storedLocale(packLocales?: PackLocales): string | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored && offerableLocales(packLocales).includes(stored) ? stored : null;
  } catch {
    return null;
  }
}

function readStoredLocale(preferred?: string | null, chosen?: string | null, packLocales?: PackLocales): string {
  const offered = offerableLocales(packLocales);
  // the customer's own case-scoped choice (a link re-opened on another phone) wins over this device's memory
  if (chosen && offered.includes(chosen)) return chosen;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && offered.includes(stored)) return stored;
  } catch {
    /* storage unavailable — default */
  }
  if (preferred && offered.includes(preferred)) return preferred;
  return 'en';
}

export function LocaleProvider({
  children,
  preferred,
  chosen,
  packLocales,
}: {
  children: ReactNode;
  preferred?: string | null;
  chosen?: string | null;
  /** VC-12: the case's pack languages (exchange summary `packLocales`); absent on India cases. */
  packLocales?: PackLocales;
}): JSX.Element {
  const [locale, setLocaleState] = useState<string>(() => readStoredLocale(preferred, chosen, packLocales));
  const packKey = Array.isArray(packLocales) ? packLocales.join(',') : '';
  const offered = useMemo(() => offerableLocales(packLocales), [packKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // VC-12: the pack's languages arrive with the exchange, after the first render. A language this device remembered
  // (Arabic, from an earlier Gulf case) becomes valid only now; a language the new case does not offer (Arabic on an
  // India case) is dropped back to the stored/preferred/English choice — never left on screen.
  useEffect(() => {
    setLocaleState((prev) => (offered.includes(prev) && prev !== 'en' ? prev : readStoredLocale(preferred, chosen, packLocales)));
  }, [packKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // SPEC_CAPTURE_UX_R2 §2.2: the case-scoped choice arrives with the exchange — honour it (and remember it here) once known.
  useEffect(() => {
    if (!chosen || !offered.includes(chosen)) return;
    setLocaleState(chosen);
    try {
      window.localStorage.setItem(STORAGE_KEY, chosen);
    } catch {
      /* non-persistent is fine */
    }
  }, [chosen]);

  // The exchange summary carries the representative's locale from case creation; honour it once
  // when nothing was chosen on this device yet.
  useEffect(() => {
    if (!preferred) return;
    try {
      if (window.localStorage.getItem(STORAGE_KEY)) return;
    } catch {
      /* fall through */
    }
    if (offered.includes(preferred)) setLocaleState(preferred);
  }, [preferred, offered]);

  const dir = dirOf(locale);
  // VC-12: lang + dir at the ROOT, so the whole document — portals, sheets and the browser's own form controls — mirrors.
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.setAttribute('data-locale', locale);
    document.documentElement.setAttribute('dir', dir);
  }, [locale, dir]);

  const setLocale = useCallback((next: string) => {
    if (!offered.includes(next)) return;
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* non-persistent is fine */
    }
  }, [offered]);

  const t = useCallback(
    (key: string, vars?: Record<string, string>): string => {
      const deck: Record<string, string> | undefined = DECKS[locale];
      let text = deck?.[key] ?? (SV_COPY as Record<string, string>)[key] ?? key;
      if (vars) for (const [name, value] of Object.entries(vars)) text = text.replace(`{${name}}`, value);
      return text;
    },
    [locale],
  );

  const value = useMemo(() => ({ locale, dir, offered, packLocales, setLocale, t }), [locale, dir, offered, packLocales, setLocale, t]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useT(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useT() outside <LocaleProvider> — mount it in App');
  return ctx;
}

/** Render `**bold**` markers as <b> — the ONLY markup decks may carry. */
export function rich(text: string): ReactNode {
  const parts = text.split('**');
  if (parts.length < 3) return text;
  return parts.map((part, i) => (i % 2 === 1 ? <b key={`${i}-${part}`}>{part}</b> : part));
}
